import * as React from 'react'
import {
  Box, Typography, Button, IconButton, TextField, MenuItem, InputAdornment, Chip, Alert, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined'

import {sectionCardSx, primaryBtnSx, secondaryBtnSx, kpiLabelSx, cellSx, headerCellSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, MUTED, WARNING, WHITE, withOpacity} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'

import {kg, int, pct} from '../format'
import {RULES} from '../data/reference'
import {
  basketWorking, observedBaskets, findBasket, basketKey, packById, packLabel, withinTolerance, round2,
} from './state/packingModel'
import Figure from './components/Figure'
import DistributionBars, {shipments} from './components/DistributionBars'
import KeyValue from '../components/KeyValue'
import EmptyState from '../components/EmptyState'
import {chipSx} from '../components/tone'

/**
 * Weight Freeze (v2) — Packing Task 8: the multi-SKU basket calculator.
 *
 * The mockup's own section map calls this "the point of the screen": a
 * merchant assembles a basket of SKUs and quantities, the calculator shows
 * the chargeable weight that basket implies, and — where this exact basket
 * is one the account has actually shipped — that computed figure sits next
 * to what those real shipments actually billed at.
 *
 * THE HONEST-EMPTINESS REQUIREMENT. `COMBOS[].itemSkus` resolves for only 3
 * of the dataset's 60 combos and `priceable` is true for only 2, because
 * `findBasket`'s own exact-match rule is deliberately literal — most combo
 * item LABELS never textually match a catalogue SKU code, and this module
 * does not fuzzy-match one onto the other (see packingModel.js's own header
 * and `findBasket`'s doc comment). That means the "computed vs actually
 * billed" band below has very little shipped history to show against, and
 * this file's whole job is to say exactly that rather than paper over it:
 *   - the candidate list under "Start from a basket this account has
 *     shipped" is `observedBaskets(state)` — already the honest, filtered
 *     set (priceable, 2+ legs, every leg still in the catalogue);
 *   - the line under that list states the real ratio (2 of 60) with live
 *     counts, never a rounded or hand-wavy version of it;
 *   - `findBasket` returns `{exact: null, near: null}` for any basket this
 *     account has not shipped, and this file renders that as prose
 *     ("This set of SKUs does not appear...") rather than a blank panel or
 *     a `0` that could be mistaken for a measured figure;
 *   - `basketComparison` below (the pure function this task's own brief
 *     specifies) returns `null` — not a zero-difference comparison — when
 *     there is nothing to compare against, and `gapPct` is `null` rather
 *     than `0` when the observed weight itself is zero, since a percentage
 *     of zero is not a real number.
 * Nothing here loosens the exact-match rule, fuzzy-matches on item names, or
 * invents a leg to make a basket priceable that findBasket says it is not.
 *
 * THE `dws` RENAME. The mockup's own KeyValue row reads `combo.dws` and
 * labels it "Shipments with DWS images". This dataset's own honesty rule
 * (see data/noberoFreeze.js's own header) is that `dws`/`sorterCount` is a
 * COURIER SORTER READING, never a photograph — there are no images behind
 * any `source: 'courier'` record. So the row below reads `combo.sorterCount`
 * under the label "Shipments with a courier sorter reading", not "images".
 *
 * NO STATE HANDOFF to Initiate. The mockup has none (section map, gap 4) and
 * the CTA below is a plain `onNavigate('initiate')` call — inventing a
 * hand-off channel would be new work this task was not asked to do.
 *
 * NO MERCHANT-SWITCH RESET. The mockup reset its basket on `m.id` change
 * because that component stayed mounted across merchants. This port has
 * exactly one merchant for the lifetime of the session, so there is nothing
 * to reset on a merchant change — the effect is dropped rather than ported
 * as a no-op `useEffect`.
 *
 * `picked` (the SKUs+quantities being assembled), `packId` (the material
 * selected for THIS basket) and the two search boxes are ephemeral
 * calculator input, not module state: no other section reads them, and they
 * are exactly the kind of section-local UI state `PackingPage.js`'s own
 * header says is fine to hold locally (as opposed to `skus`/`combos`/
 * `packs`/etc., which live in the one store above the section switch).
 */

const panelAlertSx = {fontFamily: FONT, fontSize: SIZE.xs, py: 0.75, '& .MuiAlert-message': {lineHeight: 1.6}}
const subtleSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.5}
const captionSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.6}
const monoSx = {fontFamily: 'monospace', fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, color: NAVY}
const monoClipSx = {
  ...monoSx, display: 'block', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}
const rowCardSx = {
  border: `1px solid ${BORDER}`, borderRadius: '10px', p: 1, bgcolor: WHITE,
  display: 'flex', alignItems: 'center', gap: 1,
}
const cardSx = {border: `1px solid ${BORDER}`, borderRadius: '10px', p: 1.5, bgcolor: WHITE}
const fieldSx = {fontFamily: FONT, fontSize: SIZE.xs}
const subheadingSx = {...kpiLabelSx, mb: 0.75}
// Menu is portalled to document.body, outside the shell's zoom — matches
// disputes-v2/FilterBar.js's own selectMenuProps so an open menu lines up
// with its now-SIZE['2xs'] closed control.
const selectMenuProps = {PaperProps: {sx: {'& .MuiMenuItem-root': {fontFamily: FONT, fontSize: SIZE['2xs']}}}}
/** Deliberately NOT a Figure — the brief calls for "a warn panel", visually distinct from the Figure beside it so the merchant reads it as a gap in data, not as a second measurement. */
const nothingPanelSx = {
  minWidth: 0, p: 1, borderRadius: '10px',
  border: `1px solid ${withOpacity(WARNING, 0.4)}`, bgcolor: withOpacity(WARNING, 0.1),
}

/**
 * The four honest explanations for `working.governs` — 'none' (no
 * volumetric figure to compare against), 'dead', 'volumetric' or 'level'
 * (the two agree within the shared tolerance). The tolerance value is
 * always read from `RULES.WEIGHT_TOLERANCE_KG`, never a literal 0.005, so
 * this copy can never drift from the number the comparison band actually
 * uses.
 */
const GOVERNS_COPY = {
  none: 'The packing material chosen for this basket carries no dimensions, so there is no volumetric figure '
    + 'to weigh against dead weight. Dead weight alone decides the chargeable weight.',
  dead: 'Dead weight is the larger of the two figures here, so it governs the chargeable weight. The volumetric '
    + 'weight of the material sits below it and adds nothing on top.',
  volumetric: 'The volumetric weight of the packing material is the larger of the two figures here, so it '
    + 'governs the chargeable weight, not the goods inside it. A smaller box is the only thing that brings this down.',
  level: `Dead weight and volumetric weight land within the shared ${RULES.WEIGHT_TOLERANCE_KG} kg tolerance of `
    + 'each other here, so the two agree on the chargeable weight rather than one overriding the other.',
}

/**
 * Computed chargeable weight against the weight this exact basket really
 * billed at. Null when there is nothing measured to compare against — a
 * missing comparison is not a zero-difference comparison, and rendering it
 * as one would tell the merchant their arithmetic was confirmed when
 * nothing confirmed it.
 */
export function basketComparison(working, exact) {
  if (!exact) return null
  const signed = round2(working.chargeKg - exact.mpw)
  const agrees = withinTolerance(working.chargeKg, exact.mpw)
  return {
    agrees,
    diff: agrees ? 0 : Math.abs(signed),
    heavier: signed > 0,
    gapPct: exact.mpw ? (Math.abs(signed) / exact.mpw) * 100 : null,
    ships: exact.ships,
    mpw: exact.mpw,
  }
}

/**
 * The basket a candidate/near-miss combo represents, as `{sku, qty}` pairs
 * this calculator's own picker can load. Reads `itemSkus`/`items` exactly
 * the way packingModel.findBasket's own (unexported) `asItems` does — this
 * is NOT a second definition of anything findBasket derives (exactness,
 * tolerance, the arithmetic all stay there); it is only the UI's own need
 * to turn "the combo the merchant clicked" into picker state.
 */
function itemsFor(combo) {
  return (combo.itemSkus || []).map((sku, i) => ({sku, qty: (combo.items[i] && combo.items[i].qty) || 1}))
}

/** 'N SKUs, M shipments, most probable X kg, spread Y%' — the sub-line both the candidate list and the near-miss card use, so the two can never phrase the same fact two different ways. */
function summaryLine(combo) {
  return `${int(combo.itemSkus.length)} SKUs, ${shipments(combo.ships)}, most probable ${kg(combo.mpw)}, spread ${pct(combo.spread)}`
}

/**
 * The multi-SKU basket calculator — Packing Task 8. `state` is the narrow
 * slice `PackingPage` threads down from the one module-wide store (read
 * only, never mutated here: this panel raises no request and edits no SKU,
 * pack or combo of its own). `onNavigate` is the same rail-navigation
 * callback every other Packing panel already receives.
 */
export default function BasketCalculator({state, onNavigate}) {
  const {skus, combos, packs} = state

  const [picked, setPicked] = React.useState([]) // [{sku, qty}]
  const [packId, setPackId] = React.useState(() => (packs[0] ? packs[0].id : ''))
  const [addQuery, setAddQuery] = React.useState('')

  const skusByCode = React.useMemo(() => {
    const map = {}
    skus.forEach((s) => { map[s.sku] = s })
    return map
  }, [skus])

  const pack = packById(packs, packId)
  const working = basketWorking({picked, skusByCode, pack})

  const candidates = React.useMemo(() => observedBaskets(state).slice(0, 4), [state])
  const totalCombos = combos.length
  const priceableCombos = combos.filter((c) => c.priceable).length

  const {exact, near} = findBasket(combos, working.live)
  const comparison = basketComparison(working, exact)

  const addOrBump = (sku) => {
    setPicked((prev) => {
      const idx = prev.findIndex((p) => p.sku === sku)
      if (idx === -1) return [...prev, {sku, qty: 1}]
      return prev.map((p, i) => (i === idx ? {...p, qty: p.qty + 1} : p))
    })
  }
  const incQty = (sku) => setPicked((prev) => prev.map((p) => (p.sku === sku ? {...p, qty: p.qty + 1} : p)))
  const decQty = (sku) => setPicked((prev) => prev.map((p) => (p.sku === sku && p.qty > 1 ? {...p, qty: p.qty - 1} : p)))
  const removeSku = (sku) => setPicked((prev) => prev.filter((p) => p.sku !== sku))

  /** Loads a combo's own recorded basket AND its own recorded material — never just the SKUs — so "Load"/"Switch to that basket" hands the merchant a like-for-like comparison rather than one they have to re-assemble by hand. */
  const loadBasket = (combo) => {
    setPicked(itemsFor(combo))
    if (combo.packId) setPackId(combo.packId)
  }

  const addNeedle = addQuery.trim().toLowerCase()
  const addMatches = (addNeedle
    ? skus.filter((s) => `${s.sku} ${s.sub}`.toLowerCase().indexOf(addNeedle) >= 0)
    : skus
  ).slice(0, 7)

  return (
    <Box sx={sectionCardSx}>
      <Box sx={{mb: 1.5}}>
        <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.sm, color: NAVY}}>
          Multi-SKU basket calculator
        </Typography>
        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.25, lineHeight: 1.6, maxWidth: 760}}>
          Assemble the SKUs and quantities that go out together in one parcel. The working on the right computes
          a chargeable weight from the base data; where this exact basket is one the account has actually
          shipped, the band below compares that computed figure against what those real shipments billed at.
        </Typography>
      </Box>

      <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: GAP.lg, alignItems: 'start'}}>
        {/* LEFT COLUMN — building the basket */}
        <Box sx={{display: 'grid', gap: GAP.lg, alignContent: 'start'}}>
          <Box>
            <Typography sx={subheadingSx}>Start from a basket this account has shipped</Typography>
            <Box sx={{display: 'grid', gap: 1}}>
              {candidates.length === 0 && (
                <Typography sx={subtleSx}>No priced multi-SKU basket has shipped on this account yet.</Typography>
              )}
              {candidates.map((c) => {
                const isLoaded = basketKey(working.live) === basketKey(itemsFor(c))
                return (
                  <Box key={c.key} sx={rowCardSx}>
                    <Box sx={{minWidth: 0, flex: 1}}>
                      <Typography title={c.key} sx={monoClipSx}>{c.key}</Typography>
                      <Typography sx={subtleSx}>{summaryLine(c)}</Typography>
                    </Box>
                    {isLoaded ? (
                      <Chip size='small' label='Loaded' sx={chipSx('info')} />
                    ) : (
                      <Button
                        size='small'
                        sx={secondaryBtnSx}
                        aria-label={`Load ${c.key} into the calculator`}
                        onClick={() => loadBasket(c)}
                      >
                        Load
                      </Button>
                    )}
                  </Box>
                )
              })}
            </Box>
            {/* The honest-emptiness line, with live counts — never a rounded
                or paraphrased version of the real ratio. See this file's
                own header for why the 2-of-60 gap exists and stays exact. */}
            <Typography sx={{...subtleSx, mt: 1}}>
              {`${int(priceableCombos)} of ${int(totalCombos)} observed baskets can be priced here. The rest are `
                + 'real multi-SKU parcels whose lines are recorded under labels that do not match a catalogue SKU '
                + 'code, so there is no dead weight to sum for them.'}
            </Typography>
          </Box>

          <Box>
            <Typography sx={subheadingSx}>SKUs in this shipment</Typography>
            <Box sx={{display: 'grid', gap: 1}}>
              {picked.length === 0 && (
                <Typography sx={subtleSx}>No SKUs picked yet — load a basket above or add one below.</Typography>
              )}
              {picked.map((p) => {
                const sRec = skusByCode[p.sku]
                return (
                  <Box key={p.sku} sx={rowCardSx}>
                    <Box sx={{minWidth: 0, flex: 1}}>
                      <Typography sx={monoSx}>{p.sku}</Typography>
                      <Typography sx={subtleSx}>
                        {sRec ? `${sRec.sub}, dead weight ${kg(sRec.deadKg)}` : 'No longer in this catalogue'}
                      </Typography>
                    </Box>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.25}}>
                      <IconButton
                        size='small'
                        aria-label={`Remove one unit of ${p.sku}`}
                        disabled={p.qty <= 1}
                        onClick={() => decQty(p.sku)}
                      >
                        <RemoveIcon sx={{fontSize: 14}} />
                      </IconButton>
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY, minWidth: 22, textAlign: 'center', fontVariantNumeric: 'tabular-nums'}}>
                        {p.qty}
                      </Typography>
                      <IconButton size='small' aria-label={`Add one more unit of ${p.sku}`} onClick={() => incQty(p.sku)}>
                        <AddIcon sx={{fontSize: 14}} />
                      </IconButton>
                    </Box>
                    <IconButton size='small' aria-label={`Remove ${p.sku} from this basket`} onClick={() => removeSku(p.sku)}>
                      <CloseIcon sx={{fontSize: 14}} />
                    </IconButton>
                  </Box>
                )
              })}
            </Box>
          </Box>

          <Box>
            <Typography sx={subheadingSx}>Add a SKU</Typography>
            <TextField
              size='small'
              placeholder='Search SKU or sub-category'
              value={addQuery}
              onChange={(e) => setAddQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon sx={{fontSize: 16, color: SUBTLE}} />
                  </InputAdornment>
                ),
              }}
              inputProps={{'aria-label': 'Search SKUs to add to this basket'}}
              sx={{...fieldSx, mb: 1, width: '100%'}}
            />
            <Box sx={{display: 'grid', gap: 1}}>
              {addMatches.map((s) => {
                const already = picked.some((p) => p.sku === s.sku)
                return (
                  <Box key={s.sku} sx={rowCardSx}>
                    <Box sx={{minWidth: 0, flex: 1}}>
                      <Typography sx={monoSx}>{s.sku}</Typography>
                      <Typography sx={subtleSx}>{s.sub}</Typography>
                    </Box>
                    <Button
                      size='small'
                      sx={secondaryBtnSx}
                      aria-label={`${already ? 'Add one more of' : 'Add'} ${s.sku}`}
                      onClick={() => addOrBump(s.sku)}
                    >
                      {already ? 'One more' : 'Add'}
                    </Button>
                  </Box>
                )
              })}
            </Box>
          </Box>

          <TextField
            select
            required
            size='small'
            label='Packing material'
            value={packId}
            onChange={(e) => setPackId(e.target.value)}
            helperText='Volumetric weight is anchored to the material, not to the SKUs packed inside it.'
            sx={fieldSx}
            SelectProps={{MenuProps: selectMenuProps}}
          >
            {packs.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {`${p.label}  (${p.volKg == null ? 'no dimensions' : `${kg(p.volKg)} volumetric`})`}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* RIGHT COLUMN — the working */}
        <Box>
          {!working.enough ? (
            <EmptyState
              icon={ShoppingBasketOutlinedIcon}
              title='Pick at least two SKUs'
              body='A chargeable weight for a single item is already on the base data table. This panel is for baskets that ship together.'
            />
          ) : (
            <Box sx={{display: 'grid', gap: GAP.md}}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Line</TableCell>
                    <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Qty</TableCell>
                    <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Dead weight</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {working.lines.map((line) => (
                    <TableRow key={line.sku}>
                      <TableCell sx={cellSx}>
                        <Typography sx={monoSx}>{line.sku}</Typography>
                        <Typography sx={subtleSx}>{line.sub}</Typography>
                      </TableCell>
                      <TableCell sx={{...cellSx, textAlign: 'right'}}>{int(line.qty)}</TableCell>
                      <TableCell sx={{...cellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                        {line.qty > 1 ? `${kg(line.dead)} × ${int(line.qty)} = ${kg(line.total)}` : kg(line.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={cellSx}>
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>
                        Packing material — {packLabel(packs, packId)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{...cellSx, textAlign: 'right'}}>1</TableCell>
                    <TableCell sx={{...cellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums'}}>
                      {kg(working.packSelfKg)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <KeyValue items={[
                {label: 'Summed dead weight', value: kg(working.deadTotal)},
                {
                  label: (
                    <Box component='span' sx={{display: 'inline-flex', alignItems: 'center', gap: 0.4}}>
                      Volumetric weight of the material
                      <Tooltip title='Volumetric weight is a property of the box, not of what is inside it, so it does not add up across the SKUs.'>
                        <HelpOutlineIcon sx={{fontSize: 13, color: MUTED}} />
                      </Tooltip>
                    </Box>
                  ),
                  value: working.volKg == null ? 'Not applicable' : kg(working.volKg),
                },
                {
                  label: 'Chargeable weight, the higher of the two',
                  value: (
                    <Typography component='span' sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.lg, color: NAVY, fontVariantNumeric: 'tabular-nums'}}>
                      {kg(working.chargeKg)}
                    </Typography>
                  ),
                },
              ]} />

              <Alert severity={working.governs === 'volumetric' ? 'warning' : 'info'} sx={panelAlertSx}>
                {GOVERNS_COPY[working.governs]}
              </Alert>
            </Box>
          )}
        </Box>
      </Box>

      {/* THE COMPARISON BAND — only once there is a working figure to compare. */}
      {working.enough && (
        <Box sx={{mt: GAP.lg, pt: GAP.lg, borderTop: `1px solid ${BORDER}`, display: 'grid', gap: GAP.md}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
            <Typography sx={{...kpiLabelSx, mb: 0}}>Computed against what these shipments actually billed at</Typography>
            <Chip
              size='small'
              label={exact ? 'Observed combination' : 'Not an observed combination'}
              sx={chipSx(exact ? 'ok' : 'warn')}
            />
          </Box>

          <Box sx={{display: 'flex', flexWrap: 'wrap', gap: GAP.lg}}>
            <Figure label='Computed chargeable weight' value={kg(working.chargeKg)} />
            {exact ? (
              <Figure
                label='Observed most probable weight'
                value={kg(exact.mpw)}
                tone={comparison.agrees ? 'ok' : 'warn'}
              />
            ) : (
              <Box sx={nothingPanelSx}>
                <Typography sx={{...kpiLabelSx, fontSize: SIZE['2xs'], mb: 0.25}}>Observed most probable weight</Typography>
                <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.lg, color: WARNING}}>
                  Nothing to compare
                </Typography>
                <Typography sx={{...captionSx, mt: 0.25}}>
                  This exact basket has not shipped, so there is no measured weight behind it.
                </Typography>
              </Box>
            )}
          </Box>

          {exact ? (
            <>
              <Alert severity={comparison.agrees ? 'success' : 'warning'} sx={panelAlertSx}>
                {comparison.agrees ? (
                  <>
                    The computed weight matches what {int(comparison.ships)} real shipment{comparison.ships === 1 ? '' : 's'} of
                    this basket billed at, within the {RULES.WEIGHT_TOLERANCE_KG} kg tolerance this module uses everywhere.
                  </>
                ) : (
                  <>
                    The computed figure is{' '}
                    <Box component='span' sx={{fontWeight: WEIGHT.bold}}>
                      {kg(comparison.diff)} {comparison.heavier ? 'heavier' : 'lighter'}
                    </Box>
                    {' '}than the {kg(comparison.mpw)} these {int(comparison.ships)} shipment{comparison.ships === 1 ? '' : 's'} actually
                    billed at, {comparison.gapPct == null
                      ? 'a gap that cannot be expressed as a percentage of a zero observed weight'
                      : `a gap of ${pct(comparison.gapPct)}`}.{' '}
                    {comparison.heavier
                      ? 'Check the dead weights and the declared material for this basket — one of them is likely why the computed figure runs heavy.'
                      : 'If this figure were put forward as-is, it would be argued at review. The observed weight is the safer starting point.'}
                  </>
                )}
              </Alert>

              <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: GAP.md}}>
                <Box sx={cardSx}>
                  <Typography sx={subheadingSx}>What this basket actually billed at</Typography>
                  <DistributionBars hist={exact.hist} mpw={exact.mpw} />
                  <Typography sx={{...captionSx, mt: 1}}>
                    Blue is the most probable weight, amber is every shipment charged something else. This is
                    measured invoice data, not a projection.
                  </Typography>
                </Box>

                <Box sx={cardSx}>
                  <KeyValue items={[
                    {label: 'Shipments of this basket', value: int(exact.ships)},
                    {label: 'Weight spread', value: pct(exact.spread)},
                    {label: 'Material on record for it', value: packLabel(packs, exact.packId)},
                    {label: 'Material you selected', value: packLabel(packs, packId)},
                    // The `dws` rename — see this file's own header. This is
                    // a courier sorter reading, never a photograph.
                    {label: 'Shipments with a courier sorter reading', value: int(exact.sorterCount)},
                  ]} />
                  {packId !== exact.packId && (
                    <Typography sx={{...captionSx, mt: 1}}>
                      You are computing against a different material from the one on record for this basket,
                      which is most of why the two figures differ.
                    </Typography>
                  )}
                </Box>
              </Box>
            </>
          ) : (
            <>
              <Alert severity='warning' sx={panelAlertSx}>
                This set of SKUs does not appear in this account&apos;s shipment history, so{' '}
                <Box component='span' sx={{fontWeight: WEIGHT.bold}}>
                  {kg(working.chargeKg)} is arithmetic, not a measured weight
                </Box>
                {' '}— there is no real shipment yet to check it against.
              </Alert>

              {near && (
                <Box sx={cardSx}>
                  <Typography sx={subheadingSx}>The closest thing in the data</Typography>
                  <Typography title={near.key} sx={monoClipSx}>{near.key}</Typography>
                  <Typography sx={subtleSx}>{summaryLine(near)}</Typography>
                  <Typography sx={{...captionSx, mt: 0.5, mb: 1}}>
                    This basket ships the same SKUs, only at different quantities, so it is not quite the same parcel.
                  </Typography>
                  <Button size='small' sx={secondaryBtnSx} onClick={() => loadBasket(near)}>Switch to that basket</Button>
                </Box>
              )}
            </>
          )}

          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1}}>
            <Typography sx={subtleSx}>A combo-level freeze is raised from initiate weight freeze.</Typography>
            <Button sx={primaryBtnSx} onClick={() => onNavigate('initiate')}>Take this into a freeze request</Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}
